import HelpButton from "@/components/HelpButton/HelpButton";
import NoImpactBanner from "@/components/NoImpactBanner/NoImpactBanner";
import { PageHeader } from "@/components/PageHeader/PageHeader";
import styles from "@/pages/dashboard/Dashboard.module.scss";
import { Button } from "antd";
import { Link, useNavigate } from "react-router-dom";
import MetricSelector from "../components/MetricSelector/MetricSelector";
import YearPicker from "../components/YearPicker/YearPicker";
import { useDashboardYear } from "../hooks/useDateRange";
import useImpactMetric from "../hooks/useImpactMetric";
import { useDashboardOrderCounts } from "../hooks/usePoSo";
import { useTransportImpactTotals } from "../hooks/useTransportImpact";
import TransportImpactPerTransport from "./widgets/TransportImpactDetail";
import TransportImpactPerScope from "./widgets/TransportImpactPerScope";
import TransportImpactPerType from "./widgets/TransportImpactPerType";
import TransportImpactTopContributors from "./widgets/TransportImpactTopContributors";
import TransportImpactTotal from "./widgets/TransportImpactTotal";
import { useCompanyAccess } from "@/hooks/useCompanySettings";
import PremiumExplore from "@/components/PremiumFeatureComponents/PremiumExplore";

export default function TransportDashboard() {
  const metric = useImpactMetric();
  const navigate = useNavigate();
  const access = useCompanyAccess();

  const { data: orderCounts } = useDashboardOrderCounts({});
  const { data: totalImpact } = useTransportImpactTotals({ metrics: [metric.key] });
  const { year } = useDashboardYear();
  const pageTitle = `Transport impact`;
  const pageDescription = `Insight into the part of your total company impact attributed to transport.`;

  if (access && !access.includes("lca"))
    return (
      <PremiumExplore
        title={pageTitle}
        description={pageDescription}
      />
    );

  return (
    <div>
      <PageHeader
        title={pageTitle}
        description={pageDescription}
        right={
          <>
            <YearPicker />
            <HelpButton url="https://help.root-sustainability.com/en/articles/9899105-transport-impact-dashboard" />
          </>
        }
      />
      <MetricSelector
        value={metric.key}
        onChange={(key) => navigate("/dashboard/transport/" + key)}
      />
      {orderCounts?.total === 0 ? (
        <NoImpactBanner title={`No transport impact for ${year}`}>
          <span>Start with uploading purchase and sales orders:</span>
          <Link to="/orders/purchase_orders">
            <Button type="primary">Upload orders</Button>
          </Link>
        </NoImpactBanner>
      ) : totalImpact?.transport_impact[metric.key] === 0 ? (
        <NoImpactBanner title={`No transport impact for ${year}`}>
          <span>What to do?</span>
          <ul>
            <li>
              Upload <Link to="/transport/inbound">inbound</Link> and{" "}
              <Link to="/transport/outbound">outbound</Link> transport information
            </li>
            <li>Match the transport modes with their impact</li>
          </ul>
        </NoImpactBanner>
      ) : (
        <div className={styles.grid}>
          <div className={styles["two-columns"]}>
            <div>
              <TransportImpactTotal metric={metric} />
              {metric.key === "carbon_footprint" && <TransportImpactPerScope />}
              <TransportImpactPerType metric={metric} />
            </div>
            <div>
              <TransportImpactTopContributors metric={metric} />
            </div>
          </div>
          <div className={styles["one-column"]}>
            <TransportImpactPerTransport metric={metric} />
          </div>
        </div>
      )}
    </div>
  );
}
